import { FormsyInput } from '@thanx/uikit/input'
import { Notification } from '@thanx/uikit/notification'
import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import {
  type BrameCampaign,
  useCreateBrameCampaignMutation,
  useUpdateBrameCampaignMutation,
} from 'api/brameCampaigns'
import { getErrorMessage } from 'api/utils'
import Formsy from 'formsy-react'
import { buildTranslate } from 'locales'
import { useCallback } from 'react'
import { Col, Container, Row } from 'react-bootstrap-five'
import { autoCorrectBrameCampaignUrl } from 'utilities/campaignUrlValidations'

const t = buildTranslate('games')

interface AddGameFormProps {
  onSave: () => void
  editingGame?: BrameCampaign | null
}

const AddGameForm = ({ onSave, editingGame }: AddGameFormProps) => {
  const [createBrameCampaign, { isLoading: isCreating, error: createError }] =
    useCreateBrameCampaignMutation()
  const [updateBrameCampaign, { isLoading: isUpdating, error: updateError }] =
    useUpdateBrameCampaignMutation()

  const isLoading = isCreating || isUpdating
  const apiError = createError || updateError

  const handleSubmit = useCallback(
    async (formData: { gameName: string; brameGameUrl: string }) => {
      try {
        const campaignData = {
          name: formData.gameName.trim(),
          campaign_url: autoCorrectBrameCampaignUrl(
            formData.brameGameUrl.trim()
          ),
        }

        if (editingGame) {
          await updateBrameCampaign({
            id: editingGame.id,
            ...campaignData,
          }).unwrap()
        } else {
          await createBrameCampaign(campaignData).unwrap()
        }

        onSave()
      } catch (error) {
        // Error is handled by RTK Query state, no need to handle here
        // eslint-disable-next-line no-console
        console.error('Failed to save game:', error)
      }
    },
    [editingGame, createBrameCampaign, updateBrameCampaign, onSave]
  )

  return (
    <Container className="pl-0 pt-xl">
      <Row>
        <Col>
          <Text.Header3 className="mb-xl">
            {t('addForm.content.title')}
          </Text.Header3>

          <Switch condition={Boolean(apiError)}>
            <div className="mb-4">
              <Notification size="full" kind="negative">
                {getErrorMessage(apiError!, t('addForm.errors.submit'))}
              </Notification>
            </div>
          </Switch>

          <div
            className="bg-white p-4"
            style={{
              border: '1px solid #e9eaec',
              borderRadius: '4px',
            }}
          >
            <Formsy onValidSubmit={handleSubmit}>
              <FormsyInput
                className="mb-4 uikit-input"
                name="gameName"
                label={t('addForm.fields.gameName')}
                value={editingGame?.name || ''}
                disabled={isLoading}
                validations={{ isRequired: true }}
                validationErrors={{
                  isRequired: t('addForm.validation.gameNameRequired'),
                }}
              />

              <FormsyInput
                className="mb-2 uikit-input"
                name="brameGameUrl"
                type="url"
                label={t('addForm.fields.brameGameUrl')}
                value={editingGame?.campaign_url || ''}
                disabled={isLoading}
                validations={{
                  isRequired: true,
                  isBrameCampaignUrl: true,
                }}
                validationErrors={{
                  isRequired: t('addForm.validation.brameGameUrlRequired'),
                  isBrameCampaignUrl: t(
                    'addForm.validation.invalidBrameCampaignUrl'
                  ),
                }}
              />
              <Text.BodyText4 color="grey70" className="mb-4">
                {t('addForm.fields.brameGameUrlExample')}
              </Text.BodyText4>

              <div className="d-flex justify-content-end mt-4">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {t('addForm.header.saveButton')}
                </button>
              </div>
            </Formsy>
          </div>
        </Col>
      </Row>
    </Container>
  )
}

AddGameForm.displayName = 'AddGameForm'

export default AddGameForm
